import { Box } from "@mui/material";
import { useEffect, useRef } from "react";
import { useMicLevel } from "../../hooks/useMicLevel";
import {
  buildBlobPath,
  createBlobDrawable,
  setBlobRadiusRange,
  updateBlob,
  type BlobDrawableState,
} from "./blobDrawable";
import { getMicVolumeHaloCanvasSx, getMicVolumeHaloRootSx } from "./styles";

export type MicVolumeHaloProps = {
  stream: MediaStream | null;
  active: boolean;
  buttonPx: number;
  color?: string;
};

// Mirrors Telegram RecordCircle: SCALE_*_MIN + k * amplitude (capped growth).
const LAYERS = [
  {
    n: 8,
    radiusFactor: 0.52,
    formDp: 0.12,
    scaleMin: 1.02,
    scaleAmp: 0.28,
    fillAlpha: 0.14,
    strokeAlpha: 0.95,
    speedScale: 1.02,
  },
  {
    n: 10,
    radiusFactor: 0.52,
    formDp: 0.16,
    scaleMin: 1.12,
    scaleAmp: 0.4,
    fillAlpha: 0.09,
    strokeAlpha: 0.9,
    speedScale: 1.01,
  },
  {
    n: 12,
    radiusFactor: 0.52,
    formDp: 0.18,
    scaleMin: 1.22,
    scaleAmp: 0.52,
    fillAlpha: 0.05,
    strokeAlpha: 0.85,
    speedScale: 1.0,
  },
] as const;

export const MicVolumeHalo = ({
  stream,
  active,
  buttonPx,
  color = "51, 144, 236",
}: MicVolumeHaloProps) => {
  const { levelRef } = useMicLevel(stream, active && Boolean(stream));
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const blobsRef = useRef<BlobDrawableState[] | null>(null);
  const ampRef = useRef(0);
  const lastTsRef = useRef(0);

  // Canvas fits capped outer scale (~1.75× + stroke).
  const canvasPx = Math.ceil(buttonPx * 2.2);

  useEffect(() => {
    if (!active) {
      blobsRef.current = null;
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext("2d");
      if (canvas && ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
      return;
    }

    // Same idea as Telegram: minRadius ≈ button halo, maxRadius grows with form.
    blobsRef.current = LAYERS.map((layer) => {
      const minR = buttonPx * layer.radiusFactor;
      const maxR = minR + buttonPx * layer.formDp;
      return createBlobDrawable(layer.n, minR, maxR);
    });
    ampRef.current = 0;
    lastTsRef.current = performance.now();

    let rafId = 0;

    const tick = (now: number) => {
      const canvas = canvasRef.current;
      const blobs = blobsRef.current;
      if (!canvas || !blobs) return;

      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const cssSize = canvasPx;
      const pxSize = Math.round(cssSize * dpr);
      if (canvas.width !== pxSize || canvas.height !== pxSize) {
        canvas.width = pxSize;
        canvas.height = pxSize;
      }

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      const dt = Math.min(32, now - lastTsRef.current);
      lastTsRef.current = now;

      const target = levelRef.current;
      const diff = target - ampRef.current;
      const step =
        diff > 0
          ? diff * Math.min(1, dt / 90)
          : diff * Math.min(1, dt / 180);
      ampRef.current = Math.max(0, Math.min(1, ampRef.current + step));
      const amplitude = ampRef.current;

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, cssSize, cssSize);

      const cx = cssSize / 2;
      const cy = cssSize / 2;

      for (let i = LAYERS.length - 1; i >= 0; i -= 1) {
        const layer = LAYERS[i];
        const blob = blobs[i];

        // Widen form range with loudness (water warp); near-circle when silent.
        const minR = buttonPx * layer.radiusFactor;
        const form = buttonPx * layer.formDp * (0.15 + amplitude * 0.85);
        setBlobRadiusRange(blob, minR, minR + form);
        updateBlob(blob, amplitude, layer.speedScale);

        const scale = layer.scaleMin + layer.scaleAmp * amplitude;
        const path = buildBlobPath(blob, cx, cy);

        ctx.save();
        ctx.translate(cx, cy);
        ctx.scale(scale, scale);
        ctx.translate(-cx, -cy);

        // Glass fill
        ctx.fillStyle = `rgba(${color}, ${layer.fillAlpha})`;
        ctx.fill(path);

        // Sharp light border (as on Telegram glass waves)
        ctx.strokeStyle = `rgba(255, 255, 255, ${layer.strokeAlpha})`;
        ctx.lineWidth = 1.5 / scale;
        ctx.lineJoin = "round";
        ctx.stroke(path);

        // Soft outer rim for glass depth
        ctx.strokeStyle = `rgba(${color}, ${0.35 + amplitude * 0.2})`;
        ctx.lineWidth = 0.75 / scale;
        ctx.stroke(path);

        ctx.restore();
      }

      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(rafId);
      blobsRef.current = null;
    };
  }, [active, buttonPx, canvasPx, color, levelRef]);

  if (!active) return null;

  return (
    <Box sx={getMicVolumeHaloRootSx(canvasPx)} aria-hidden>
      <Box
        component="canvas"
        ref={canvasRef}
        sx={getMicVolumeHaloCanvasSx()}
      />
    </Box>
  );
};
